/// <amd-module name="Search/Code/Components/ZeroData" />

import * as React from "react";
import * as Container from "Search/Shared/Base/Container";
import * as ZeroData from "Search/Shared/Components/ZeroData";
import * as Constants from "Search/Code/Constants";
import * as SharedResources from "Search/Shared/Resources";
import * as Resources from "Search/Code/Resources";
import * as Utils_Accessibility from 'VSS/Core/Util/Accessibility';
import { Link } from "VSSUI/Link";
import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { ScenarioType } from "Search/Shared/Components/ZeroData/ZeroData.Props";
import { ActionCreator } from "Search/Code/Flux/ActionCreator";
import { StoresHub, IAggregatedState, StoreName } from "Search/Code/Flux/StoresHub"

export const ZeroDataContainer = Container.create<StoreName, ActionCreator, IAggregatedState, StoresHub>(
    [
        "zeroDataStore"
    ],
    ({
        searchStoreState,
        zeroDataState
    }, props) => {
        const { scenario } = zeroDataState;
        if (scenario && scenario !== ZeroData.ScenarioType.None) {
            const { activityId, query, error } = searchStoreState;
            const {
                notifyResultsRendered,
                publishZeroData,
                onPreviewLoaded,
                notifySearchFailed,
                fetchMoreResults,
                notifyFeedbackMailLinkClicked
            } = props.actionCreator;
            let zeroDataProps = {} as ZeroData.IZeroDataProps;
            if (scenario === ZeroData.ScenarioType.LandingPage) {
                zeroDataProps.message = Resources.CodeWelcomeText;
                zeroDataProps.help = "";
                zeroDataProps.announceMessage = Resources.CodeWelcomeText;
            }
            else {
                zeroDataProps =
                    getZeroDataProps(
                        () => fetchMoreResults(query.skipResults, Constants.CodeSearchMaxSkipOrTakeResults),
                        query.searchText,
                        activityId,
                        scenario,
                        notifyFeedbackMailLinkClicked,
                        props.isHosted);
            }

            return <ZeroData.ZeroData
                {...zeroDataProps}
                pageContext={props.pageContext}
                onDidMount={
                    () => {
                        Utils_Accessibility.announce(zeroDataProps.announceMessage, true);
                        publishZeroData(ZeroData.ScenarioType[scenario], error);
                        onDidMount(scenario, notifyResultsRendered, notifySearchFailed, onPreviewLoaded)
                    }
                } />;
        }

        return null;
    });

function getZeroDataProps(
    onFetchMore: () => void,
    queryText: string,
    actId: string,
    scenarioType: ScenarioType,
    onFeedbackMailLinkCliked: () => void,
    isHosted: boolean): ZeroData.IZeroDataProps {
    let zeroDataProps = {} as ZeroData.IZeroDataProps;

    if (scenarioType === ZeroData.ScenarioType.ServiceError) {
        const messageText: string = SharedResources.ServiceErrorMessage;
        const feedbackLink = format(Constants.CodexFeedbackMailToLinkFormat, actId)

        zeroDataProps.scenario = ZeroData.ScenarioType.ServiceError;
        zeroDataProps.message = messageText;
        zeroDataProps.help = ZeroData.getServiceErrorHelp(isHosted, feedbackLink);
        zeroDataProps.announceMessage = messageText;
    } else {
        zeroDataProps.scenario = scenarioType;
        if (zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResults ||
            zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithFilter ||
            zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithWildcardAndFilter) {
            zeroDataProps.message = getZeroDataMessage(queryText, Resources.ZeroCodeResultsMessageFormat);
            let helpMessage: string =
                zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResults ||
                    zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithFilter ?
                    SharedResources.ZeroResultsWithFilterHelpText : SharedResources.ZeroResultsWithWildcardAndFilterHelpText;
            zeroDataProps.help = getZeroDataHelp(isHosted, helpMessage, actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = format(Resources.ZeroCodeResultsMessageFormat, queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.EmptyQueryNotSupported) {
            zeroDataProps.message = getZeroDataMessage(queryText, SharedResources.EmptyQueryMessage);
            zeroDataProps.help = getZeroDataHelp(isHosted, SharedResources.EmptyQueryHelpText, actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = SharedResources.EmptyQueryMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.OnlyWildcardQueryNotSupported) {
            zeroDataProps.message = getZeroDataMessage(queryText, SharedResources.OnlyWildcardQueryNotSupportedMessage);
            zeroDataProps.help = getZeroDataHelp(isHosted, SharedResources.OnlyWildcardQueryNotSupportedHelpText, actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = SharedResources.OnlyWildcardQueryNotSupportedMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithWildcard ||
            zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithNoWildcardNoFilter) {
            zeroDataProps.message = getZeroDataMessage(queryText, Resources.ZeroCodeResultsWithoutFiltersMessageFormat);
            let helpMessage: string = (zeroDataProps.scenario === ZeroData.ScenarioType.ZeroResultsWithWildcard) ? "" : SharedResources.ZeroResultsWithNoWildcardNoFilterHelpText;
            zeroDataProps.help = getZeroDataHelp(isHosted, helpMessage, actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = format(Resources.ZeroCodeResultsWithoutFiltersMessageFormat, queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.PrefixWildCardNotSupported) {
            zeroDataProps.message = ZeroData.getQueryNotSupportedHelp(queryText, SharedResources.WildCardNotSupportedHelpFormat);
            zeroDataProps.help = SharedResources.WildCardNotSupportedMessage;
            zeroDataProps.announceMessage = format(SharedResources.WildCardNotSupportedHelpFormat, queryText);
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.PhraseQueriesWithCEFacetsNotSupported) {
            zeroDataProps.message = ZeroData.getQueryNotSupportedHelp(queryText, Resources.PhraseAndCEFacetsNotSupportedMessage);
            zeroDataProps.help = SharedResources.ZeroResultsWithWildcardAndFilterHelpText;
            zeroDataProps.announceMessage = Resources.PhraseAndCEFacetsNotSupportedMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.WildcardQueriesWithCEFacetsNotSupported) {
            zeroDataProps.message = ZeroData.getQueryNotSupportedHelp(queryText, Resources.WildcardAndCEFacetsNotSupportedMessage);
            zeroDataProps.help = SharedResources.ZeroResultsWithWildcardAndFilterHelpText;
            zeroDataProps.announceMessage = Resources.WildcardAndCEFacetsNotSupportedMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.NoPermission) {
            zeroDataProps.message = Resources.ZeroCodeResultsNoPermissionMessage;
            zeroDataProps.help = ZeroData.getAccessPermissionHelp(Constants.VersionControlPermissionsBlogLink);
            zeroDataProps.announceMessage = Resources.ZeroCodeResultsNoPermissionMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.NoPermissionWithShowMore) {
            const help: JSX.Element =
                <FormatComponent format={Resources.ZeroCodeResultsNoPermissionWithShowMoreHelpFormat}>
                    {
                        <Link href={Constants.VersionControlPermissionsBlogLink} target="_blank">{SharedResources.CheckAccessPermissionLabel}</Link>
                    }
                    {
                        <Link onClick={onFetchMore}>{SharedResources.ShowMoreLabel}</Link>
                    }
                </FormatComponent>;

            zeroDataProps.message = Resources.ZeroCodeResultsNoPermissionMessageWithShowMore;
            zeroDataProps.help = help;
            zeroDataProps.announceMessage = Resources.ZeroCodeResultsNoPermissionMessageWithShowMore;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.NoPermissionAfterShowMore) {
            zeroDataProps.message = Resources.ZeroCodeResultsNoPermissionMessageAfterShowMore;
            zeroDataProps.help = ZeroData.getAccessPermissionHelp(Constants.VersionControlPermissionsBlogLink);
            zeroDataProps.announceMessage = Resources.ZeroCodeResultsNoPermissionMessageAfterShowMore;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.IndexingScenario) {
            const feedbackLink = format(Constants.CodexFeedbackMailToLinkFormat, actId);
            zeroDataProps.message = SharedResources.AccountIndexingMessage;
            zeroDataProps.help = ZeroData.getIndexingHelp(isHosted, feedbackLink);
            zeroDataProps.announceMessage = SharedResources.AccountIndexingMessage;
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.UnsupportedProximitySearchTerm){
            zeroDataProps.message = Resources.UnsupportedProximitySearchTerm;
            zeroDataProps.help = Resources.UnsupportedProximitySearchHelpText;
            zeroDataProps.announceMessage = Resources.UnsupportedProximitySearchTerm;
        } else if(zeroDataProps.scenario === ZeroData.ScenarioType.SubStringTooShort){
            zeroDataProps.message = Resources.SubStringTooShort;
            zeroDataProps.help = getZeroDataHelp(isHosted, "", actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = Resources.SubStringTooShort;
        } else if(zeroDataProps.scenario === ZeroData.ScenarioType.SubStringWithInfixWildcard ||
            zeroDataProps.scenario === ZeroData.ScenarioType.CEFFacetSubstringWithInfixWildcard){
            zeroDataProps.message = Resources.SubStringSearchWithInfixWildCard;
            zeroDataProps.help = getZeroDataHelp(isHosted, "", actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = Resources.SubStringSearchWithInfixWildCard;            
        } else if(zeroDataProps.scenario === ZeroData.ScenarioType.CEFFacetSubstring){
            zeroDataProps.message = Resources.SubStringSearchCEF;
            zeroDataProps.help = getZeroDataHelp(isHosted, "", actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = Resources.SubStringSearchCEF;              
        } else if (zeroDataProps.scenario === ZeroData.ScenarioType.QuestionMarkWildcardSubstring) {
            zeroDataProps.message = Resources.QuestionMarkWildcardSubstring;
            zeroDataProps.help = getZeroDataHelp(isHosted, "", actId, onFeedbackMailLinkCliked);
            zeroDataProps.announceMessage = Resources.QuestionMarkWildcardSubstring;
        }
    }

    return zeroDataProps;
}

function getZeroDataMessage(
    queryText: string,
    messageText: string): JSX.Element {
    const message: JSX.Element =
        <FormatComponent format={messageText}>
            {
                <span className="searchText">
                    {queryText}
                </span>
            }
        </FormatComponent>;

    return message;
}

function getZeroDataHelp(
    isHosted: boolean,
    helpText: string,
    activityId: string,
    notifyFeedbackMailLinkClicked: () => void
): JSX.Element {
    const feedbackLink = format(Constants.CodexFeedbackMailToLinkFormat, activityId);
    const learnMoreLinkAriaLabel = format(SharedResources.LearnMoreAriaLabel, Resources.CodeSearchAriaLabel);
    const help: JSX.Element = ZeroData.getZeroResultsHelpComponent(
        isHosted,
        helpText,
        Resources.ZeroCodeResultsLearnMoreFormat,
        Constants.LearnMoreLink,
        learnMoreLinkAriaLabel,
        feedbackLink,
        notifyFeedbackMailLinkClicked);

    return help;
}

function onDidMount(
    zeroDataScenario: ZeroData.ScenarioType,
    onResultsRendered: () => void,
    onSearchFailed: () => void,
    notifyPreviewLoaded: (isSuccess: boolean) => void): void {
    const searchFailed = zeroDataScenario === ZeroData.ScenarioType.ServiceError;

    if (!searchFailed) {
        onResultsRendered();
        notifyPreviewLoaded(true);
    } else {
        onSearchFailed();
    }
}
